'use client'
import Link from 'next/link'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTheme } from 'next-themes'
import { ArrowLeft, ArrowRight } from '@carbon/icons-react'
import Autoplay from 'embla-carousel-autoplay'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { ChromaText } from '@/components/ui/chroma-text'
import { ArchitectureBento } from '@/components/architecture-bento'
import {
  ProductThumb,
  type ProductThumbVariant,
} from '@/components/product-thumb'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@/components/ui/carousel'

interface Card {
  title: string
  body: string
  image: string
  tags: string[]
  link: string
  /** Draws the monogram thumbnail instead of `image`; see ProductThumb. */
  thumb?: ProductThumbVariant
}

const defaultCards: Card[] = [
  {
    title: 'IntelQI UDC',
    body: 'Unlock intelligent data insights with our unified data center and analytics platform.',
    image: '/illustrations/intelqi_udc.webp',
    tags: ['Analytics', 'Data Center', 'Business'],
    link: '/products/udc',
    thumb: 'udc',
  },
  {
    title: 'Accounting',
    body: 'Streamline your financial operations with comprehensive accounting tools and real-time insights.',
    image: '/illustrations/accounting.webp',
    tags: ['Finance', 'Reporting', 'Compliance'],
    link: '/products/accounts',
    thumb: 'accounting',
  },
  {
    title: 'LMS',
    body: 'Empower your team with our learning management system for continuous growth and development.',
    image: '/illustrations/lms.webp',
    tags: ['Education', 'Training', 'Development'],
    link: '/products/lms',
    thumb: 'lms',
  },
  {
    title: 'HRMS',
    body: 'Manage your workforce efficiently with our integrated human resource management system.',
    image: '/illustrations/hrms.webp',
    tags: ['HR', 'Employee', 'Recruitment'],
    link: '/products/hrms',
    thumb: 'hrms',
  },
  {
    title: 'Payroll',
    body: 'Simplify payroll processing with automated calculations and compliance management.',
    image: '/illustrations/payroll.webp',
    tags: ['Salary', 'Tax Management', 'Automation'],
    link: '/products/payroll',
    thumb: 'payroll',
  },
  {
    title: 'School Management System',
    body: 'A complete digital platform for managing students, parents, faculty, academics, communication, and school operations from one centralized system.',
    image: '/illustrations/school.webp',
    tags: ['School ERP', 'Academics', 'Administration'],
    link: '/schoolerp',
    thumb: 'school',
  },
]

const CARD_INTERVAL = 3500
const PAUSE_AFTER_MANUAL = 8000

// ── Main component ─────────────────────────────────────────────────────────────
// Carousel mechanics (drag, snap, momentum, click-suppression after drags) are
// Embla's; React only mirrors the bits the UI renders from (dots, arrows,
// off-stage dimming) and gates autoplay on visibility/reduced-motion.
export const FeatureShowcase = ({
  cards = defaultCards,
}: {
  cards?: Card[]
}) => {
  const { theme, resolvedTheme } = useTheme()
  const [isDark, setIsDark] = useState(false)

  useEffect(() => {
    setIsDark((resolvedTheme || theme) === 'dark')
  }, [theme, resolvedTheme])

  // stopOnInteraction stays false so hover/focus stops resume on leave; manual
  // navigation instead rests the carousel through our longer 8s timer below.
  const autoplayRef = useRef(
    Autoplay({
      delay: CARD_INTERVAL,
      stopOnInteraction: false,
      stopOnMouseEnter: true,
      stopOnFocusIn: true,
    })
  )
  // The shadcn <Carousel /> owns the Embla instance; we grab its api via setApi
  // and mirror the same handful of bits the UI renders from.
  const [api, setApi] = useState<CarouselApi>()

  // ── Embla state mirrored into React ────────────────────────────────────────
  // Initial values reproduce the server-rendered first paint: first snap
  // selected, first two slides staged (matches the md breakpoint markup).
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [snapCount, setSnapCount] = useState(cards.length)
  const [stagedSlides, setStagedSlides] = useState<number[]>([0, 1])
  const [canPrev, setCanPrev] = useState(false)
  const [canNext, setCanNext] = useState(true)

  // Autoplay gates
  const rootRef = useRef<HTMLDivElement | null>(null)
  const inViewRef = useRef(true)
  const reducedMotionRef = useRef(false)
  const pauseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Manual navigation (arrows, dots, drags) rests the carousel for longer
  // than the autoplay delay, then resumes — unless the section left the
  // viewport or the user prefers reduced motion.
  const pauseAutoplay = useCallback(() => {
    const ap = api?.plugins().autoplay
    if (!ap) return
    ap.stop()
    if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current)
    pauseTimerRef.current = setTimeout(() => {
      pauseTimerRef.current = null
      if (inViewRef.current && !reducedMotionRef.current) ap.play()
    }, PAUSE_AFTER_MANUAL)
  }, [api])

  useEffect(() => {
    if (!api) return
    const onSelect = () => {
      setSelectedIndex(api.selectedScrollSnap())
      setCanPrev(api.canScrollPrev())
      setCanNext(api.canScrollNext())
    }
    const onSlidesInView = () => setStagedSlides(api.slidesInView())
    const onReInit = () => {
      setSnapCount(api.scrollSnapList().length)
      onSelect()
      onSlidesInView()
    }
    onReInit()
    api.on('select', onSelect)
    api.on('reInit', onReInit)
    api.on('slidesInView', onSlidesInView)
    api.on('pointerDown', pauseAutoplay)
    return () => {
      api.off('select', onSelect)
      api.off('reInit', onReInit)
      api.off('slidesInView', onSlidesInView)
      api.off('pointerDown', pauseAutoplay)
    }
  }, [api, pauseAutoplay])

  // Autoplay runs only while the section is on screen and motion is allowed.
  useEffect(() => {
    if (!api) return
    const ap = api.plugins().autoplay
    if (!ap) return

    reducedMotionRef.current = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches
    if (reducedMotionRef.current) {
      ap.stop()
      return
    }

    const el = rootRef.current
    if (!el) return
    const ob = new IntersectionObserver(([entry]) => {
      const visible = entry?.isIntersecting ?? true
      inViewRef.current = visible
      if (!visible) ap.stop()
      else if (!pauseTimerRef.current) ap.play()
    })
    ob.observe(el)
    return () => ob.disconnect()
  }, [api])

  useEffect(() => {
    return () => {
      if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current)
    }
  }, [])

  // ── Navigation handlers ────────────────────────────────────────────────────
  const handlePrev = useCallback(() => {
    api?.scrollPrev()
    pauseAutoplay()
  }, [api, pauseAutoplay])
  const handleNext = useCallback(() => {
    api?.scrollNext()
    pauseAutoplay()
  }, [api, pauseAutoplay])
  const handleDotClick = useCallback(
    (index: number) => {
      api?.scrollTo(index)
      pauseAutoplay()
    },
    [api, pauseAutoplay]
  )

  // Bordered pagination pill — rendered in the left rail
  // on desktop and under the carousel on mobile. Before Embla initialises,
  // snapCount falls back to cards.length so the server markup renders every
  // dot, with the surplus mobile-only dot hidden by CSS on md+; after init
  // the count comes straight from Embla's snap list.
  const paginationDots = (
    <div
      className={cn(
        'inline-flex items-center gap-1.5 px-3 py-2 rounded-full backdrop-blur-md',
        isDark ? 'bg-white/5' : 'bg-card/70'
      )}
    >
      {Array.from({ length: snapCount }, (_, index) => (
        <button
          key={index}
          type="button"
          onClick={() => handleDotClick(index)}
          className={cn(
            'h-1.5 rounded-full transition-all duration-300',
            snapCount === cards.length &&
              index >= cards.length - 1 &&
              'md:hidden',
            selectedIndex === index
              ? 'w-6 bg-emerald-600 dark:bg-primary-active'
              : isDark
                ? 'w-1.5 bg-white/30 hover:bg-white/50'
                : 'w-1.5 bg-[#3A4048]/30 hover:bg-[#3A4048]/50'
          )}
          aria-label={`Go to slide ${index + 1}`}
          aria-current={selectedIndex === index ? 'true' : undefined}
        />
      ))}
    </div>
  )

  return (
    <div className="rounded-br-5xl" ref={rootRef}>
      {/* ── Product Cards ─────────────────────────────────────────────────────── */}
      <section
        className={cn(
          // overflow-x-clip: the carousel bleeds to the viewport's right edge
          // (100vw math), which must not create horizontal page scroll
          'pt-20 sm:pt-24 lg:pt-32 overflow-x-clip',
          'bg-background'
        )}
        aria-labelledby="feature-showcase-heading"
      >
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            {/* Left */}
            <div className="lg:col-span-4 mb-1 lg:mb-0">
              <h2
                id="feature-showcase-heading"
                className={cn(
                  'font-heading text-[32px] md:text-[42px] font-normal leading-tight mb-4 md:mb-6',
                  isDark ? 'text-primary-active' : 'text-foreground'
                )}
              >
                <ChromaText>Explore Our Product Suite</ChromaText>
              </h2>
              <p
                className={cn(
                  'mb-6 md:mb-8 text-[16px] md:text-[18px] opacity-75 leading-[1.7]',
                  isDark ? 'text-muted-foreground' : 'text-muted-foreground'
                )}
              >
                From concept to deployment; explore how our team designs,
                develops, and delivers impactful digital systems for clients{' '}
                {/* /industries and its six sector pages had no inbound link
                    anywhere on the site; this sentence already named them. */}
                <Link
                  href="/industries"
                  className="underline decoration-emerald-600/40 underline-offset-4 transition-colors hover:decoration-emerald-600 dark:decoration-primary-active/40 dark:hover:decoration-primary-active"
                >
                  across diverse industries
                </Link>
                .
              </p>
              <Link
                href="/products"
                aria-label="View all products"
                className={cn(
                  'group inline-flex items-center gap-2 text-[16px] md:text-[18px] font-medium',
                  isDark ? 'text-primary-active' : 'text-foreground'
                )}
              >
                View All Products
                <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>

              {/* Pagination — lives in the rail on desktop */}
              <div className="hidden lg:block mt-8">{paginationDots}</div>
            </div>

            {/* Right carousel */}
            <div className="lg:col-span-8 relative pb-4">
              {/* shadcn <Carousel /> is the anchor for the floating arrows —
                  the arrows are its direct children so they can straddle the
                  masked viewport edges. Bleeds to the viewport's right edge:
                  the negative margins cancel the container's responsive gutter;
                  at 2xl the container caps at 1400px so the leftover half-gap
                  is added too. */}
              <Carousel
                setApi={setApi}
                opts={{
                  align: 'start',
                  containScroll: 'trimSnaps',
                  // The peeking next card (~72px sliver) must not count as "in
                  // view" — it renders dimmed and its link leaves the tab order.
                  inViewThreshold: 0.6,
                  duration: 22,
                }}
                plugins={[autoplayRef.current]}
                aria-label="Product suite"
                className="lg:-mr-8 2xl:mr-[calc((1400px-100vw)/2-2rem)]"
              >
                {/* Horizontal-only clip so cards dissolve at both edges instead
                    of hard-cropping; Embla owns horizontal panning while the
                    browser keeps vertical page scroll and pinch-zoom. Leading
                    pad leaves room for the masked left sliver of the previous
                    card; trailing pad lets the last snap rest with breathing
                    room instead of clamping flush against the faded edge. */}
                <CarouselContent
                  viewportClassName={cn(
                    'cursor-grab active:cursor-grabbing',
                    'lg:[-webkit-mask-image:linear-gradient(to_right,transparent,black_24px,black_calc(100%-48px),transparent)]',
                    'lg:[mask-image:linear-gradient(to_right,transparent,black_24px,black_calc(100%-48px),transparent)]'
                  )}
                  viewportStyle={{
                    overflowX: 'clip',
                    overflowY: 'visible',
                    touchAction: 'pan-y pinch-zoom',
                  }}
                  className="ml-0 gap-4 md:gap-6 pb-2 pr-4 md:pl-6 md:pr-24"
                >
                  {cards.map((c, index) => {
                    const isVisible = stagedSlides.includes(index)
                    return (
                      <CarouselItem
                        key={index}
                        className={cn(
                          'pl-0',
                          // Width via CSS breakpoints so the server-rendered
                          // first paint is correct on phones (no post-hydration
                          // reflow).
                          //
                          // The min-width is a floor, not a style choice: a
                          // percentage basis let the card fall to 203px at a
                          // 1024 viewport, which pushed the three tags onto
                          // three separate rows and added 65px to every card.
                          // 340px is the width at which three tags still fit
                          // one row, so the tag block stops being a variable.
                          // Narrower than that the track simply scrolls — it
                          // is a carousel.
                          'basis-full md:min-w-[340px] md:basis-[calc(50%-60px)]'
                          // No staging fade: the card's text, tags and button
                          // must never animate — the tile mosaic in the
                          // thumbnail is the only moving part. Off-stage cards
                          // still leave the tab order below.
                        )}
                      >
                        <article className="rounded-xl relative flex flex-col h-full group overflow-hidden bg-muted dark:bg-muted/60">
                          <Link
                            href={c.link}
                            className="flex flex-col flex-1"
                            // Off-stage cards stay clickable-looking but leave
                            // the tab order: focusing them would natively
                            // scroll the track out from under Embla
                            tabIndex={isVisible ? undefined : -1}
                            aria-hidden={!isVisible}
                          >
                            {/* Monogram thumbnail where the card declares a
                                variant; cards passed in without one keep their
                                illustration. Full-bleed to the card edges —
                                no inner border or padding, like the Insights
                                cards. */}
                            <div className="relative h-[200px] w-full overflow-hidden">
                              {c.thumb ? (
                                <ProductThumb variant={c.thumb} />
                              ) : (
                                <img
                                  src={c.image}
                                  alt={c.title}
                                  draggable={false}
                                  className="h-full w-full object-cover pointer-events-none"
                                />
                              )}
                              {/* Same grain pass as the solutions deck's art
                                  halves — one texture across both carousels */}
                              <div
                                aria-hidden
                                className="pointer-events-none absolute inset-0"
                                style={{
                                  backgroundImage: 'url(/gradients/grain.webp)',
                                  backgroundRepeat: 'repeat',
                                  backgroundSize: '512px 512px',
                                  mixBlendMode: 'overlay',
                                  opacity: 0.4,
                                }}
                              />
                            </div>
                            {/* Card body — padding on the text only.

                                NOTHING in this card may vary with the
                                viewport. Its height has to be a single
                                constant, because browser zoom changes the
                                CSS-px width and any width-dependent value
                                — a reflowing line count, a `md:` type step,
                                a `md:` padding step — makes the whole
                                carousel resize as the user zooms.

                                So: no `md:` variants anywhere below, one
                                type size per element, and the two variable
                                text blocks reserve a fixed number of lines
                                in `em` (2 x 1.25 leading = 2.5em for the
                                title, 4 x 1.7 = 6.8em for the body) with a
                                line-clamp as the backstop. Card height is
                                514px at every width and every zoom level. */}
                            <div className="flex flex-1 flex-col p-5">
                              <h3 className="font-heading text-[20px] font-normal mb-2 text-foreground leading-[1.25] line-clamp-2 min-h-[2.5em]">
                                {c.title}
                              </h3>
                              <p className="text-[14.5px] text-muted-foreground leading-[1.7] line-clamp-4 min-h-[6.8em]">
                                {c.body}
                              </p>
                              {/* mt-auto pins the tag row (and the CTA under
                                  it) to the card's bottom, so they sit on the
                                  same line on every card no matter how long
                                  the description runs. */}
                              <div className="mt-auto flex flex-wrap gap-2 pt-4">
                                {c.tags.map((tag, idx) => (
                                  <span
                                    key={idx}
                                    className="px-2.5 py-1 rounded-[4px] backdrop-blur-md text-[11px] font-medium bg-emerald-600/10 text-foreground"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                              <span className="pt-6 inline-flex items-center gap-1.5 text-[15px] font-medium text-foreground w-fit">
                                Learn more
                                <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
                              </span>
                            </div>
                          </Link>
                        </article>
                      </CarouselItem>
                    )
                  })}
                </CarouselContent>

                {/* Floating circular arrows, straddling the carousel edges.
                    Each fades out at its bound — no left arrow on the first
                    view, no right arrow on the last. */}
                {/* Centred on the seam between the 210px artwork band and
                    the card body — the arrows straddle the separation
                    instead of landing on titles or artwork. */}
                <Button
                  size="icon"
                  onClick={handlePrev}
                  aria-label="Previous product"
                  disabled={!canPrev}
                  className="hidden lg:inline-flex absolute -left-5 top-[210px] -translate-y-1/2 z-30"
                >
                  <ArrowLeft className="size-4" />
                </Button>
                <Button
                  size="icon"
                  onClick={handleNext}
                  aria-label="Next product"
                  disabled={!canNext}
                  className="hidden lg:inline-flex absolute right-2 top-[210px] -translate-y-1/2 z-30"
                >
                  <ArrowRight className="size-4" />
                </Button>
              </Carousel>

              {/* Pagination — under the carousel on mobile/tablet only */}
              <div className="flex justify-center items-center mt-5 lg:hidden">
                {paginationDots}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Banner ─────────────────────────────────────────────────────────────── */}
      <section className={cn('pt-16 sm:pt-20 md:pt-28')}>
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 md:gap-4">
            <div>
              {/* h2, not h1 — the page's h1 lives in the hero */}
              <h2
                className={cn(
                  'font-heading font-normal leading-tight',
                  'text-3xl md:text-5xl',
                  isDark ? 'text-primary-active' : 'text-foreground'
                )}
              >
                <ChromaText>Enterprise Architecture</ChromaText>
              </h2>
              <p
                className={cn(
                  'mt-2 text-sm sm:text-lg md:text-xl lg:text-2xl opacity-75',
                  isDark ? 'text-muted-foreground' : 'text-muted-foreground'
                )}
              >
                Built for scale, security, and performance
              </p>
            </div>
            <div className="flex items-center w-full md:w-auto">
              <Link
                href="/architecture"
                aria-label="Learn about the architecture"
                className={cn(
                  'inline-flex items-center gap-2 text-base md:text-lg lg:text-xl font-medium whitespace-nowrap group transition-colors',
                  isDark
                    ? 'text-white hover:text-primary-active'
                    : 'text-foreground'
                )}
              >
                <span className="relative inline-flex items-center gap-2">
                  <span className="relative">
                    Learn about our architecture
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-emerald-600 dark:bg-primary-active transition-all duration-300 group-hover:w-full" />
                  </span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden
                    className={cn(
                      'h-5 w-5 transition-transform duration-300 group-hover:translate-x-1',
                      isDark ? 'text-primary-active' : 'text-current'
                    )}
                  >
                    <path
                      d="M6 4l8 8-8 8"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Architecture bento (blume-style) ───────────────────────────────────── */}
      <section
        className={cn(
          'bg-background',
          'pt-12 md:pt-20 pb-12 md:pb-20',
          'rounded-br-[1.5rem] md:rounded-br-[5rem]'
        )}
      >
        <div className="container mx-auto">
          <ArchitectureBento />
        </div>
      </section>
    </div>
  )
}

export default FeatureShowcase
